import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  ReaderBlockDiscussionComment,
  ReaderBlockDiscussionCommentAdd,
  ReaderBlockDiscussionToggleReplies
} from '.';

import type { CommonBlockPropsReader } from 'components/reader/blocks';
import type {
  DiscussionSubmitObj,
  DiscussionComment
} from './ReaderBlockDiscussion';

interface Props
  extends Pick<CommonBlockPropsReader<'discussion'>, 'font' | 'fontSize'> {
  allowAnonymousComments: boolean;
  childCommentsOrder: number[];
  childCommentsShowReplies: boolean;
  childCommentsShowAfterIndex: any;
  comment: DiscussionComment;
  comments: { [key: number]: DiscussionComment };
  onDelete: (
    commentId: number,
    parentCommentId?: number,
    deletionReason?: string
  ) => Promise<void>;
  onSaveChanges: (saveObj: DiscussionSubmitObj) => Promise<void>;
  onSubmit: (submitObj: DiscussionSubmitObj) => Promise<void>;
  onToggleReplies: (updateObj: { parentCommentId: number }) => void;
}

const ReaderBlockDiscussionCommentWrapper = ({
  allowAnonymousComments,
  childCommentsOrder,
  childCommentsShowReplies,
  childCommentsShowAfterIndex,
  comment,
  comments,
  font,
  fontSize,
  onDelete,
  onSaveChanges,
  onSubmit,
  onToggleReplies
}: Props) => {
  const { theme } = useReduxState();

  const { query } = useRouter();

  useEffect(() => {
    if (comment.id === parseInt(query.parentCommentId as string, 10))
      onToggleReplies({ parentCommentId: comment.id });
  }, []);

  return (
    <div className="wrapper">
      <div className="comment-wrapper" key={comment.id}>
        <ReaderBlockDiscussionComment
          comment={comment}
          font={font}
          fontSize={fontSize}
          onDelete={onDelete}
          onSaveChanges={onSaveChanges}
        />

        <div className="child-comments">
          {childCommentsOrder.length ? (
            <div className="child-comments-toggle">
              <ReaderBlockDiscussionToggleReplies
                count={
                  childCommentsShowAfterIndex ||
                  childCommentsShowAfterIndex === 0
                    ? childCommentsShowAfterIndex + 1
                    : childCommentsOrder.length
                }
                fontSize={fontSize}
                onClick={onToggleReplies}
                parentCommentId={comment.id}
                showReplies={childCommentsShowReplies}
              />
            </div>
          ) : null}

          {childCommentsShowReplies && childCommentsOrder.length
            ? childCommentsOrder.map((childCommentId) => (
                <div className="child-comment" key={childCommentId}>
                  <ReaderBlockDiscussionComment
                    comment={comments[childCommentId]}
                    font={font}
                    fontSize={fontSize}
                    onDelete={onDelete}
                    onSaveChanges={onSaveChanges}
                    parentCommentId={comment.id}
                  />
                </div>
              ))
            : null}

          {(childCommentsShowAfterIndex || childCommentsShowAfterIndex === 0) &&
          childCommentsOrder.length
            ? childCommentsOrder
                .filter((childCommentId, i) => i > childCommentsShowAfterIndex)
                .map((childCommentId) => (
                  <div className="child-comment" key={childCommentId}>
                    <ReaderBlockDiscussionComment
                      comment={comments[childCommentId]}
                      font={font}
                      fontSize={fontSize}
                      onDelete={onDelete}
                      onSaveChanges={onSaveChanges}
                      parentCommentId={comment.id}
                    />
                  </div>
                ))
            : null}

          <ReaderBlockDiscussionCommentAdd
            allowAnonymousComments={allowAnonymousComments}
            isChildForm={true}
            fontSize={fontSize}
            onSubmit={onSubmit}
            parentCommentId={comment.id}
          />
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          margin-top: 20px;
        }
        .comment-wrapper {
          background-color: white;
          border-radius: 7px;
          padding: 15px;
        }
        .child-comments {
          display: ${childCommentsShowReplies ||
          childCommentsShowAfterIndex ||
          childCommentsShowAfterIndex === 0
            ? 'block'
            : 'flex'};
          justify-content: flex-end;
          flex-wrap: wrap;
          padding-top: 10px;
        }
        .child-comment {
          background-color: ${theme.lightGrey};
          border-radius: 7px;
          margin-top: 15px;
          padding: 12px;
        }
        .child-comments-toggle {
          flex-grow: 1;
          margin-right: 10px;
          margin-top: 15px;
        }
        .reply {
          margin-left: 0px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .child-comments {
            margin-left: 0px;
          }
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussionCommentWrapper;
